import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildProductPath } from '../utils/productSlug';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import usePageView from '../hooks/usePageView';
import { MAX_CONTENT_WIDTH } from '../constants/layout';

const heroURL =
  'https://asset.bloomnation.com/ar_700:700,c_fill,d_vendor:global:catalog:product:image.png,f_auto,fl_preserve_transparency,g_auto,q_auto,w_1400/v1747611257/vendor/9683/catalog/product/2/0/20250210145516_file_67aa135478d6f';

const parseDate = value =>
  !value ? null : value.toDate ? value.toDate() : new Date(value);

// The loading state has to reserve the full tile footprint immediately.
// If the frame or any grid ancestor is allowed to shrink-wrap, the skeletons
// collapse into tiny squares until the real image dimensions arrive.
function FadeInImage({ src, alt, onClick }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className={`gallery-image-container${!loaded ? ' shimmer' : ''}`}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onClick={onClick}
        className={`gallery-grid-img ${loaded ? 'loaded' : ''}`}
      />
    </div>
  );
}

export default function GalleryPage() {
  usePageView('gallery');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [images, setImages] = useState([]);
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const [offsetY, setOffsetY] = useState(0);

  // This map will be: productID → [ “Roses”, “Mixed Bouquets”, … ]
  const [productToCatTitles, setProductToCatTitles] = useState({});

  const nav = useNavigate();

  // 1) Fetch categories & products once
  useEffect(() => {
    (async () => {
      const catSnap = await getDocs(collection(db, 'categories'));
      const fetchedCats = catSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCategories(fetchedCats);

      const prodSnap = await getDocs(collection(db, 'products'));
      setProducts(prodSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Build productID → [category titles] lookup
      const lookup = {};
      fetchedCats.forEach(cat => {
        // cat.itemIDs is assumed to be an array of product IDs
        ;(cat.itemIDs || []).forEach(pid => {
          if (!lookup[pid]) lookup[pid] = [];
          lookup[pid].push(cat.title);
        });
      });
      setProductToCatTitles(lookup);
    })();
  }, []);

  // 2) Real-time listener for all imageItems
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'imageItems'), snap => {
      const fetched = snap.docs.map(d => {
        const x = d.data();
        return {
          id: d.id,
          caption: x.caption ?? '',
          imageURL: x.imageURL ?? '',
          thumbnailURL: x.thumbnailURL ?? '',
          productID: x.productID ?? null,
          dateAdded: parseDate(x.dateAdded),
        };
      });
      setImages(fetched);

      if (fetched.length > 0) {
        setSectionsLoaded(true);
      }
    });
    return () => unsub();
  }, []);

  // 3) Parallax scroll
  useEffect(() => {
    const handleScroll = () => {
      setOffsetY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Group images by category (for the “byCat” sections)
  const byCat = categories.map(cat => ({
    ...cat,
    images: images.filter(
      i =>
        i.productID &&
        Array.isArray(cat.itemIDs) &&
        cat.itemIDs.includes(i.productID)
    ),
  }));

  // Any images that didn’t match a category’s itemIDs
  const ungrouped = images.filter(
    i =>
      !i.productID ||
      !categories.some(
        cat =>
          Array.isArray(cat.itemIDs) && cat.itemIDs.includes(i.productID)
      )
  );

  const openProduct = pid => {
    const prod = products.find(p => p.id === pid);
    if (prod) nav(buildProductPath(prod));
  };

  const renderCard = img => {
    const prod = products.find(p => p.id === img.productID);
    const catTitles = productToCatTitles[img.productID] || [];

    return (
      <button
        key={img.id}
        type="button"
        className="gallery-grid-card"
        onClick={() => prod && openProduct(img.productID)}
      >
        <FadeInImage
          src={img.thumbnailURL || img.imageURL}
          alt={img.caption || prod?.productName || 'Floral arrangement'}
          onClick={() => prod && openProduct(img.productID)}
        />

        <div className="gallery-grid-copy">
          {(!img.caption || img.caption.trim() === '') && catTitles.length > 0 && (
            <div className="image-caption">
              {catTitles.join(', ')}
            </div>
          )}

          {prod && (
            <div className="product-name">
              {prod.productName}
            </div>
          )}

          {img.caption && img.caption.trim() !== '' && (
            <div className="image-caption">
              {img.caption}
            </div>
          )}
        </div>
      </button>
    );
  };

  const parallaxY = offsetY * 0.5;

  return (
    <div className="gallery-page">
      <div
        className="hero"
        style={{
          backgroundImage: `url(${heroURL})`,
          backgroundPosition: `center ${-parallaxY}px`,
        }}
      >
        <div className="hero-overlay" />
        <div className="hero-text">
          <p className="hero-line2">Fresh Flowers</p>
          <p className="hero-line1">Fresh Moments</p>
        </div>
      </div>

      <div className="gallery-content">
        <main className="categories">
          {byCat.map(cat =>
            cat.images.length > 0 ? (
              <section
                key={cat.id}
                className={`category ${sectionsLoaded ? 'visible' : ''}`}
              >
                <div className="cat-head">
                  <h2 className="cat-title">
                    <Link
                      to={`/category/${cat.id}`}
                      className="cat-title-link"
                    >
                      {cat.title}
                    </Link>
                  </h2>
                  {cat.subtitle && <p className="cat-sub">{cat.subtitle}</p>}
                </div>
                <div className="grid">
                  {cat.images.map(renderCard)}
                </div>
              </section>
            ) : null
          )}

          {ungrouped.length > 0 && (
            <section
              className={`category ${sectionsLoaded ? 'visible' : ''}`}
            >
              <div className="cat-head">
                <h2 className="cat-title">More Photos</h2>
              </div>
              <div className="grid">
                {ungrouped.map(renderCard)}
              </div>
            </section>
          )}
        </main>
      </div>

      <style jsx>{`
        .gallery-page {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
        }

        /* Hero banner */
        .hero {
          position: relative;
          height: 300px;
          background-size: cover;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
        }

        .hero-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #fff;
          text-align: center;
        }

        .hero-line1 {
          margin: 0;
          font-family: 'Instrument Serif', serif;
          font-weight: 900;
          font-size: 1.5rem;
        }

        .hero-line2 {
          margin: 0;
          font-family: 'Instrument Serif', serif;
          font-weight: 900;
          font-size: 2.25rem;
          font-style: italic;
        }

        /* Content container */
        /*
         Keep the gallery container chain explicitly full width while loading.
         The placeholder bug here was caused by the section/grid stack
         shrink-wrapping around unloaded image frames instead of stretching
         to the available column width.
        */
        .gallery-content {
          width: 100%;
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: 48px 16px;
          padding-left: max(16px, env(safe-area-inset-left));
          padding-right: max(16px, env(safe-area-inset-right));
          flex: 1;
          box-sizing: border-box;
        }

        .categories {
          flex: 1;
          width: 100%;
        }

        /* Animate each section */
        .category {
          display: block;
          width: 100%;
          margin-bottom: 64px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }

        .category.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .cat-head {
          margin-bottom: 16px;
          width: 100%;
        }

        .cat-title {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 700;
          font-family: 'Instrument Serif', serif;
        }

        .cat-title-link {
          color: inherit;
          text-decoration: none;
          cursor: pointer;
        }

        .cat-title-link:hover,
        .cat-title-link:focus {
          text-decoration: underline;
        }

        .cat-sub {
          margin: 4px 0 0;
          font-size: 0.875rem;
          color: #666;
        }

        .grid {
          display: grid;
          width: 100%;
          min-width: 100%;
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          row-gap: 16px;
          column-gap: 16px;
          grid-template-columns: repeat(4, 1fr);
          box-sizing: border-box;
        }

        @media (min-width: 1400px) {
          .grid {
            grid-template-columns: repeat(5, 1fr);
          }
        }

        @media (min-width: 1000px) and (max-width: 1399px) {
          .grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        @media (max-width: 999px) {
          .grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 600px) {
          .grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .gallery-grid-card {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          text-align: center;
          padding: 0;
          border: none;
          background: none;
          cursor: pointer;
          transition: transform 0.2s ease;
          width: 100%;
        }

        .gallery-grid-card:hover {
          transform: translateY(-2px);
        }

        .gallery-grid-card:focus {
          outline: 2px solid #007bff;
          outline-offset: 2px;
        }

        /*
         The image frame owns the loading size, not the image element.
         Keep the reserved square aspect ratio on the frame so each skeleton
         fills its grid cell before the image has loaded.
        */
        .gallery-image-container {
          position: relative;
          width: 100%;
          box-sizing: border-box;
          overflow: hidden;
          border: 1px solid var(--color-border-strong, #D1D5DB);
          background: var(--color-border, #e5e7eb);
        }

        .gallery-image-container.shimmer {
          background: linear-gradient(90deg, #d6d6d6 25%, #ececec 50%, #d6d6d6 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }

        .gallery-image-container::before {
          content: '';
          display: block;
          padding-top: 100%;
        }

        .gallery-grid-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.35s ease-in;
        }

        .gallery-grid-img.loaded {
          opacity: 1;
        }

        .gallery-grid-copy {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          padding-top: 8px;
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .product-name {
          margin-top: 0;
          font-size: 0.875rem;
          color: #333;
          text-align: center;
          word-break: break-word;
          font-weight: 700;
          line-height: 1.3;
        }

        .image-caption {
          margin-top: 0;
          margin-bottom: 0;
          font-size: 0.75rem;
          color: #555;
          text-align: center;
          word-break: break-word;
          line-height: 1.3;
        }
      `}</style>
    </div>
  );
}
